/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as stores from "stores";
import { Component } from "react";
import { getMessage } from "utils/TranslationUtil";
import fullScreenIcon from "dc-icons/viewer2021/SDC_FullScreenMode_18_N.svg";
import { actionTags, docViewModes, VERB_FULL_SCREEN, digSigStatus } from "constants/enums";
import { roundOffTo2Decimal } from "utils/MathUtils";
import { getDocViewMode, trackFullScreenEntryAnalytics, trackFullScreenExitAnalytics } from "utils/PageByPageViewUtils";
import { getDocumentViewHandle } from "selectors/documentViewSelectors";
import { getCurrentPageZoomLevel, getPageViewModifier } from "selectors/zoom";
import { isPanActive } from "selectors/appSelectors";
import { OVERLAY_VISIBILITY, FILL_SIGN_OCG_NAME, FIREFOX_FULLSCREEN_SHORTCUT_TOAST_TIMEOUT } from "constants/constants";
import { getAnnotationFlags, isHiddenOCG, getQuickToolsVisibility } from "selectors/documentSelectors";
import { isReadAloudActive, isReadAloudPaused } from "selectors/readAloudSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { eventNames } from "publicApis/constants";
import { isFirefox } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getPagesViewElementHandle } from "selectors/pagedViewState";

class FullScreenBootstrap extends Component {
  constructor() {
    super();
    this.prevState = {};
    this.entryTime = 0;
    this.exitTime = 0;
  }

  getLabel = () => getMessage("label.fullScreenView");

  getIcon = () => fullScreenIcon;

  getTooltip = () => null;

  getAriaLabel = () => getMessage("modernviewer.ariaLabel.fullScreenView");

  getActionTags = () => [actionTags.LOCAL];

  id = VERB_FULL_SCREEN;

  initializeAction = (opts = {}) => {
    const zoomLevel = getCurrentPageZoomLevel(stores);
    const docViewMode = getDocViewMode(stores);
    const pageViewModifier = getPageViewModifier(stores);
    const panActive = isPanActive(stores);
    const annotsFlag = getAnnotationFlags(stores);
    const isFillnSignOCGHidden = isHiddenOCG(stores, FILL_SIGN_OCG_NAME);
    const showQuickTools = getQuickToolsVisibility(stores);

    this.prevState = {
      zoomLevel: roundOffTo2Decimal(zoomLevel * 100),
      docViewMode,
      pageViewModifier,
      panActive,
      annotsFlag,
      isFillnSignOCGHidden,
      showQuickTools
    };
    this._addFullScreenChangeEvents();
  };

  executeAction = (opts = {}) => {
    const documentViewElement = getDocumentViewHandle(stores);
    if (documentViewElement) {
      let requestFullscreenPromise;
      if (documentViewElement.requestFullscreen) {
        requestFullscreenPromise = documentViewElement.requestFullscreen();
      } else if (documentViewElement.webkitRequestFullscreen) {
        /* Safari */
        requestFullscreenPromise = documentViewElement.webkitRequestFullscreen();
      } else if (documentViewElement.msRequestFullscreen) {
        /* IE11 */
        requestFullscreenPromise = documentViewElement.msRequestFullscreen();
      }

      if (requestFullscreenPromise instanceof Promise) {
        requestFullscreenPromise.catch(() => {
          // requestFullscreen API returns rejected promise if triggered from keyboard shortcut in firefox without any recent mouse click
          if (isFirefox()) {
            getMessage("firefox.fullscreen.shortcut.message").then(message => {
              stores.notificationViewStore.addNotification(message, {
                timeout: FIREFOX_FULLSCREEN_SHORTCUT_TOAST_TIMEOUT
              });
            });
          }
          this._resetState();
        });
      }
    }
  };

  _addFullScreenChangeEvents = () => {
    const documentViewElement = getDocumentViewHandle(stores);
    this._removeEventListener = documentViewElement.addEventListener("fullscreenchange", this._handleFullScreenChange);
    this._removeWebkitEventListener = documentViewElement.addEventListener(
      "webkitfullscreenchange",
      this._handleFullScreenChange
    );
    this._removeMozEventListener = documentViewElement.addEventListener(
      "mozfullscreenchange",
      this._handleFullScreenChange
    );
    this._removeMSEventListener = documentViewElement.addEventListener(
      "MSFullscreenChange",
      this._handleFullScreenChange
    );
  };

  _handleFullScreenChange = evts => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      this.exitTime = Date.now();
      const timeSpent = this.exitTime - this.entryTime;
      trackFullScreenExitAnalytics(stores, timeSpent);
      overlayEvents.trigger(eventNames.FULL_SCREEN_VIEW_EXIT, {});
      // exiting full screen view
      this._moveToPreviousState();
      this._resetState();
      // sometimes scrollEnd doesn't get called while exiting from full screen mode so calling it forcefully
      isReadAloudActive(stores) && isReadAloudPaused(stores) && stores.readAloudStore.handleScrollEnd();
    } else {
      this.entryTime = Date.now();
      trackFullScreenEntryAnalytics(stores);
      overlayEvents.trigger(eventNames.FULL_SCREEN_VIEW_ENTRY, {});
      stores.documentStore.setViewMode(docViewModes.FULL_SCREEN);
      const pagesViewHandle = getPagesViewElementHandle(stores);
      if (pagesViewHandle) {
        pagesViewHandle.focus();
      }
    }
  };

  _moveToPreviousState = () => {
    stores.documentStore.setSelectivelyRenderAnnotsFlag(this.prevState.annotsFlag);
    stores.documentStore.setQuickToolsVisibility(this.prevState.showQuickTools);
    stores.pageByPageStore.resetVisitedPages();
    this.prevState.isFillnSignOCGHidden && stores.documentStore.setOCGLayerVisibility(FILL_SIGN_OCG_NAME, false);
    // performing zoom action twice once before setting view mode and once after
    // To fix two issues :
    // 1) ADCS-33417 - first zoom action is performed so that currentPageIndex can be calculated correctly in setViewMode
    // 2) Reason of second performZoomAction - In setViewMode, for TWO_COLUMN, we are setting pageViewModifier to FIT_PAGE or FIT_WIDTH
    // that's why zoomlevel was not maintained on exit
    stores.zoom.performZoomAction({
      newZoomLevel: this.prevState.zoomLevel / 100,
      newPageViewModifier: this.prevState.pageViewModifier
    });
    stores.documentStore.setViewMode(this.prevState.docViewMode);
    stores.zoom.performZoomAction({
      newZoomLevel: this.prevState.zoomLevel / 100,
      newPageViewModifier: this.prevState.pageViewModifier
    });
    stores.documentStore.setPagesCacheWindow(this.prevState.zoomLevel / 100);
    !this.prevState.panActive && stores.appStore.setPanActive(false);
    stores.overlayDropinStore.setOverlayVisibility("COMMENT", OVERLAY_VISIBILITY.VISIBLE);
    stores.overlayDropinStore.setOverlayVisibility("fillsign", OVERLAY_VISIBILITY.VISIBLE);
  };

  _resetState = () => {
    this._removeEventListener && this._removeEventListener();
    this._removeWebkitEventListener && this._removeWebkitEventListener();
    this._removeMozEventListener && this._removeMozEventListener();
    this._removeMSEventListener && this._removeMSEventListener();
    this.prevState = {};
    this.entryTime = 0;
    this.exitTime = 0;
    stores.pageByPageStore.setFullScreenExitReason();
  };

  render() {
    return null;
  }
}

export default FullScreenBootstrap;
